import { useState, useMemo } from 'react';
import { Bell, Check, AlertTriangle, AlertOctagon, Info, CheckCircle, XCircle } from 'lucide-react';
import { Button, Card, Table, StatusBadge, TableHeader } from '@components/ui';
import { mockNotifications } from '@mocks/bildirimler';

const SEVERITY_CONFIG = {
    critical: { label: 'Kritik', icon: AlertOctagon, variant: 'error' },
    error: { label: 'Hata', icon: XCircle, variant: 'error' },
    warning: { label: 'Uyarı', icon: AlertTriangle, variant: 'warning' },
    info: { label: 'Bilgi', icon: Info, variant: 'info' },
    success: { label: 'Başarılı', icon: CheckCircle, variant: 'success' },
};

export default function Notifications() {
    const [filter, setFilter] = useState('all'); // all, unread, read
    const [severityFilter, setSeverityFilter] = useState('all');
    const [notifications, setNotifications] = useState(mockNotifications);

    const filteredNotifications = useMemo(() => {
        let result = notifications;

        if (filter === 'unread') result = result.filter(n => !n.isRead);
        if (filter === 'read') result = result.filter(n => n.isRead);
        if (severityFilter !== 'all') result = result.filter(n => n.severity === severityFilter);

        return result;
    }, [notifications, filter, severityFilter]);

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const markAsRead = (id) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    };

    const columns = [
        {
            key: 'isRead',
            header: '',
            width: 'xs',
            align: 'center',
            render: (val) => !val ? (
                <div className="w-2.5 h-2.5 bg-primary rounded-full" title="Okunmadı" />
            ) : null,
        },
        {
            key: 'severity',
            header: 'Tür',
            width: 'sm',
            align: 'center',
            render: (val) => {
                const config = SEVERITY_CONFIG[val] || SEVERITY_CONFIG.info;
                return <StatusBadge status={config.label} variant={config.variant} />;
            },
        },
        {
            key: 'title',
            header: 'Başlık',
            width: 'lg',
            render: (val, row) => (
                <span className={!row.isRead ? 'font-semibold text-text-primary' : 'text-text-secondary'}>
                    {val}
                </span>
            ),
        },
        {
            key: 'message',
            header: 'Mesaj',
            width: 'xl',
            render: (val) => (
                <span className="text-text-tertiary line-clamp-2">{val}</span>
            ),
        },
        {
            key: 'createdAt',
            header: 'Tarih',
            width: 'md',
            render: (val) => <span className="text-text-muted text-sm">{formatDate(val)}</span>,
        },
        {
            key: 'actions',
            header: '',
            width: 'sm',
            align: 'center',
            render: (val, row) => !row.isRead ? (
                <Button
                    variant="soft-primary"
                    size="xs"
                    onClick={() => markAsRead(row.id)}
                >
                    Okundu
                </Button>
            ) : (
                <span className="text-text-muted text-xs">Okundu</span>
            ),
        },
    ];

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header with filters */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Bildirimler</h1>
                    <p className="text-text-tertiary mt-1">Tüm sistem bildirimlerini buradan yönetebilirsiniz</p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full lg:w-auto">
                    <div className="flex flex-col w-full sm:w-auto min-w-[140px]">
                        <label className="text-sm font-medium text-text-secondary mb-1">Durum</label>
                        <select
                            className="w-full px-3 py-2 text-sm border border-border-light rounded-lg bg-bg-primary"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        >
                            <option value="all">Tümü</option>
                            <option value="unread">Okunmamış</option>
                            <option value="read">Okunmuş</option>
                        </select>
                    </div>

                    <div className="flex flex-col w-full sm:w-auto min-w-[140px]">
                        <label className="text-sm font-medium text-text-secondary mb-1">Bildirim Türü</label>
                        <select
                            className="w-full px-3 py-2 text-sm border border-border-light rounded-lg bg-bg-primary"
                            value={severityFilter}
                            onChange={(e) => setSeverityFilter(e.target.value)}
                        >
                            <option value="all">Tümü</option>
                            <option value="info">Bilgi</option>
                            <option value="warning">Uyarı</option>
                            <option value="error">Hata</option>
                            <option value="critical">Kritik</option>
                            <option value="success">Başarılı</option>
                        </select>
                    </div>

                    <Button
                        variant="success"
                        icon={<Check size={18} />}
                        onClick={markAllAsRead}
                    >
                        Tümünü Okundu İşaretle
                    </Button>
                </div>
            </div>

            {/* Table */}
            <Card padding="none">
                <Table
                    columns={columns}
                    data={filteredNotifications}
                    striped
                    hover
                    emptyMessage="Bildirim bulunamadı."
                    emptyIcon={<Bell size={40} />}
                />
            </Card>
        </div>
    );
}
